import api from '@api';
import {
  ArtifactRenderer,
  validate,
  type Spec
} from '@globalwebindex/spec-renderer';
import { Log } from '@enums';
import BackIcon from '@icons/back.svg?react';
import CloseIcon from '@icons/close.svg?react';
import { useArtifactsStore } from '@store/artifacts';
import { useShellStore } from '@store/shell';
import { PrimaryButton } from '@ui/buttons';
import type { DropdownMenuItemProps } from '@ui/dropdowns';
import { DropdownWithMenuItems } from '@ui/dropdowns';
import { ContentIcon } from '@ui/icons';
import * as utils from '@utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArtifactVersionSummary } from 'types/schema';
import Styled from './ArtifactSurface.styled';

interface Props {
  isEnabled?: boolean;
  onClose?: () => void;
}

function ArtifactSurface({ isEnabled = true, onClose }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const artifactRef = useRef<HTMLDivElement>(null);
  const [artifactSpec, setArtifactSpec] = useState<Spec | null>(null);
  const [versions, setVersions] = useState<ArtifactVersionSummary[]>([]);
  const [versionsStatus, setVersionsStatus] = useState<
    'idle' | 'loading' | 'resolved'
  >('idle');
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const artifactId = useArtifactsStore(
    (store) => store.currentArtifact.artifactId
  );
  const versionId = useArtifactsStore(
    (store) => store.currentArtifact.versionId
  );
  const currentTitle = useArtifactsStore(
    (store) => store.currentArtifact.title
  );
  const listedTitle = useArtifactsStore(
    (store) => store.artifacts.find((item) => item.id === artifactId)?.title
  );
  const addArtifact = useArtifactsStore((store) => store.addArtifact);
  const setCurrentArtifact = useArtifactsStore(
    (store) => store.setCurrentArtifact
  );
  const setToast = useShellStore((store) => store.setToast);
  const title = currentTitle || listedTitle;

  useEffect(() => {
    if (!isEnabled || !artifactId) {
      setVersions([]);
      setVersionsStatus('idle');
      return;
    }

    let cancelled = false;

    setVersionsStatus('loading');
    setVersions([]);

    api.artifacts
      .getVersions(artifactId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        setVersions(
          response
            .filter((version) => version.content_type === 'json')
            .sort((left, right) => right.version_number - left.version_number)
        );
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setVersions([]);
        setToast({
          message: 'Failed to load artifact versions.',
          status: 'error'
        });
        utils.log('Failed to load artifact versions.', {
          type: Log.Warning,
          id: artifactId,
          data: error
        });
      })
      .finally(() => {
        if (!cancelled) {
          setVersionsStatus('resolved');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [artifactId, isEnabled, setToast]);

  useEffect(() => {
    if (!isEnabled || !artifactId || versions.length === 0) {
      return;
    }

    const hasValidVersion =
      versionId &&
      versions.some((version) => version.artifact_version_id === versionId);

    if (!hasValidVersion) {
      setCurrentArtifact({
        artifactId,
        title,
        versionId: versions[0].artifact_version_id
      });
    }
  }, [artifactId, isEnabled, setCurrentArtifact, title, versionId, versions]);

  useEffect(() => {
    if (!isEnabled || !versionId) {
      setArtifactSpec(null);

      if (
        !isEnabled ||
        !artifactId ||
        versionsStatus === 'resolved'
      ) {
        setIsLoading(false);
      }

      return;
    }

    let cancelled = false;

    setArtifactSpec(null);
    setIsLoading(true);

    api.artifacts
      .getVersion(versionId)
      .then((version) => {
        if (cancelled) {
          return;
        }

        const { currentArtifact } = useArtifactsStore.getState();

        if (currentArtifact.artifactId !== version.artifact_id) {
          setCurrentArtifact({
            artifactId: version.artifact_id,
            title,
            versionId
          });
        }

        if (title) {
          addArtifact({ id: version.artifact_id, title });
        }

        const validated = validate(version.content);

        if (validated.success) {
          setArtifactSpec(validated.data as Spec);
        } else {
          utils.log('Spec validation failed.', {
            type: Log.Warning,
            id: version.artifact_id,
            data: validated.error?.issues
          });
          setArtifactSpec(null);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setArtifactSpec(null);
        setToast({
          message: 'Failed to load artifact.',
          status: 'error'
        });
        utils.log('Failed to load artifact version.', {
          type: Log.Warning,
          id: versionId,
          data: error
        });
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [addArtifact, artifactId, isEnabled, setCurrentArtifact, setToast, title, versionId, versionsStatus]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const element = event.target as HTMLElement;

      if (element.scrollTop > 0) {
        headerRef.current?.classList.add('shadow');
      } else {
        headerRef.current?.classList.remove('shadow');
      }
    };

    contentRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      contentRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const versionOptions = useMemo<DropdownMenuItemProps[]>(
    () =>
      versions.map((version) => ({
        label: `Version ${version.version_number}`,
        selected: version.artifact_version_id === versionId,
        value: version.artifact_version_id
      })),
    [versions, versionId]
  );

  const selectedVersionLabel = useMemo(() => {
    const selectedVersion = versions.find(
      (version) => version.artifact_version_id === versionId
    );

    if (selectedVersion) {
      return `Version ${selectedVersion.version_number}`;
    }

    return versionOptions[0]?.label ?? 'Version';
  }, [versions, versionId, versionOptions]);

  const handleChangeVersion = useCallback(
    (nextVersionId: string) => {
      if (!artifactId || nextVersionId === versionId) {
        return;
      }

      setCurrentArtifact({ artifactId, title, versionId: nextVersionId });
    },
    [artifactId, setCurrentArtifact, title, versionId]
  );

  const handleDownload = useCallback(async () => {
    if (!artifactRef.current || isDownloading) {
      return;
    }

    const filename = (title?.trim() || versionId || 'artifact')
      .replace(/[:,']/g, '')
      .replace(/[^\w-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

    setIsDownloading(true);

    try {
      await utils.downloadElementAsPng(artifactRef.current, filename, {
        padding: 32
      });
    } catch (error) {
      setToast({
        status: 'error',
        message: 'Failed to download artifact.'
      });
      utils.log('Failed to download artifact.', {
        type: Log.Warning,
        id: versionId,
        data: error
      });
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, setToast, title, versionId]);

  const isPanelView = Boolean(onClose);

  const isAwaitingVersion =
    isEnabled &&
    !versionId &&
    Boolean(artifactId) &&
    versionsStatus !== 'resolved';
  const showContentLoading = isLoading || isAwaitingVersion;

  const showVersionDropdown =
    Boolean(artifactId) &&
    (versionsStatus === 'loading' || versionOptions.length > 1);

  return (
    <>
      <Styled.Header isPanelView={isPanelView} ref={headerRef}>
        <Styled.HeaderLeading isPanelView={isPanelView}>
          <Styled.Title isPanelView={isPanelView}>Artifact</Styled.Title>
          {showVersionDropdown ? (
            <DropdownWithMenuItems
              className="artifact-version-dropdown"
              disabled={versionsStatus === 'loading'}
              options={versionOptions}
              testId="artifact-version-dropdown"
              triggerLabel={selectedVersionLabel}
              onChange={handleChangeVersion}
            />
          ) : null}
        </Styled.HeaderLeading>
        {onClose ? (
          <Styled.CloseButton onClick={onClose}>
            <BackIcon className="back icon" />
            <CloseIcon className="close icon" />
          </Styled.CloseButton>
        ) : null}
      </Styled.Header>
      <Styled.Content className="overlay-scrollbar" ref={contentRef}>
        {showContentLoading ? (
          <Styled.Description>Loading Artifact...</Styled.Description>
        ) : artifactSpec ? (
          <>
            <Styled.ArtifactToolbar>
              {title ? (
                <Styled.ArtifactTitle>{title}</Styled.ArtifactTitle>
              ) : null}
              <PrimaryButton
                data-testid="artifact-download"
                disabled={isDownloading}
                startIcon={<ContentIcon />}
                type="button"
                onClick={handleDownload}>
                {isDownloading ? 'Downloading...' : 'Download'}
              </PrimaryButton>
            </Styled.ArtifactToolbar>
            <Styled.ArtifactCapture ref={artifactRef}>
              <ArtifactRenderer spec={artifactSpec} />
            </Styled.ArtifactCapture>
          </>
        ) : (
          <Styled.Description>Artifact unavailable.</Styled.Description>
        )}
      </Styled.Content>
    </>
  );
}

export default ArtifactSurface;
